import { RECORD_STATUSES, type DataRecord } from '@/data/records';
import { TODAY } from '@/data/seed';
import { formatDate, formatWon } from '@/lib/format';
import { addDays, format, parseISO } from 'date-fns';

// 변경 이력의 순수 로직 — 저장 시 바뀐 필드를 뽑고, 더미 이력을 만들고, 최신순으로 정렬한다.
// 저장소(records-store)와 화면(history-sheet)에서 분리해 DOM 없이 테스트한다.

/** 이력으로 남기는 필드 — 편집 시트에서 바꿀 수 있는 것과 상태. */
export type HistoryField = 'endDate' | 'amount' | 'status';

export const HISTORY_FIELD_LABEL: Record<HistoryField, string> = {
  endDate: '종료일',
  amount: '금액',
  status: '상태',
};

export interface HistoryEntry {
  id: string;
  /** 변경 일시(ISO) */
  at: string;
  actor: string;
  field: HistoryField;
  /** 저장 값 그대로(날짜는 yyyy-MM-dd, 금액은 숫자 문자열) — 보여 줄 때 formatHistoryValue로 바꾼다. */
  before: string;
  after: string;
}

export interface FieldChange {
  field: HistoryField;
  before: string;
  after: string;
}

/** 이력에 보여 줄 값 — 저장 값을 화면 표기로. */
export function formatHistoryValue(field: HistoryField, value: string) {
  if (field === 'endDate') {
    return formatDate(value);
  }
  if (field === 'amount') {
    return formatWon(Number(value));
  }
  return value;
}

const TRACKED_FIELDS: HistoryField[] = ['endDate', 'amount', 'status'];

/** 저장하려는 변경(patch) 중 실제로 값이 달라지는 필드만 뽑는다. 같은 값으로 저장하면 이력이 생기지 않는다. */
export function diffRecord(before: DataRecord, patch: Partial<DataRecord>): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const field of TRACKED_FIELDS) {
    const next = patch[field];
    if (next === undefined) {
      continue;
    }
    const previous = before[field];
    if (previous !== next) {
      changes.push({ field, before: String(previous), after: String(next) });
    }
  }
  return changes;
}

let sequence = 0;

export function createEntries(
  recordId: string,
  changes: readonly FieldChange[],
  actor: string,
  now: Date,
): HistoryEntry[] {
  return changes.map((change) => {
    sequence += 1;
    return {
      id: `H-${recordId}-${String(now.getTime())}-${String(sequence)}`,
      at: now.toISOString(),
      actor,
      ...change,
    };
  });
}

/** 최신순. 일시가 같으면 나중에 쌓인 것이 위로 오게 원래 순서를 뒤집는다. */
export function sortHistoryNewestFirst(entries: readonly HistoryEntry[]) {
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => b.entry.at.localeCompare(a.entry.at) || b.index - a.index)
    .map(({ entry }) => entry);
}

const SEED_ACTORS = ['운영자 A', '운영자 B', '시스템'];

/**
 * 항목마다 0~3건의 더미 이력. 현재 값에서 거꾸로 거슬러 올라가며 만들어서,
 * 가장 최근 이력의 "이후" 값이 항상 항목의 현재 값과 맞는다. index % 4 === 0이면 이력이 없어 빈 상태도 볼 수 있다.
 */
export function seedHistory(record: DataRecord, index: number): HistoryEntry[] {
  const count = index % 4;
  const current = {
    amount: record.amount,
    endDate: record.endDate,
    statusIndex: RECORD_STATUSES.indexOf(record.status),
  };
  const entries: HistoryEntry[] = [];

  for (let step = 0; step < count; step += 1) {
    const field = TRACKED_FIELDS[(index + step) % TRACKED_FIELDS.length] ?? 'amount';
    const at = addDays(TODAY, -(step * 6 + 2 + (index % 3)));
    at.setHours(9 + ((index + step) % 9), (index * 7) % 60);
    const base = {
      id: `H-${record.id}-seed-${String(step)}`,
      at: at.toISOString(),
      actor: SEED_ACTORS[(index + step) % SEED_ACTORS.length] ?? '시스템',
    };

    if (field === 'amount') {
      const before = Math.max(10_000, current.amount - 10_000 * (1 + (step % 3)));
      entries.push({ ...base, field, before: String(before), after: String(current.amount) });
      current.amount = before;
    } else if (field === 'endDate') {
      const before = format(
        addDays(parseISO(current.endDate), -7 * (1 + (step % 2))),
        'yyyy-MM-dd',
      );
      entries.push({ ...base, field, before, after: current.endDate });
      current.endDate = before;
    } else if (current.statusIndex > 0) {
      const before = RECORD_STATUSES[current.statusIndex - 1] ?? record.status;
      entries.push({ ...base, field, before, after: RECORD_STATUSES[current.statusIndex] ?? '' });
      current.statusIndex -= 1;
    }
  }
  return entries;
}
